import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Center, Spinner } from '@chakra-ui/react'
import useFirestoreUser from '../../hooks/useFirestoreUser'
import FirestoreUser from '../../types/FirestoreUser'

/**
 * Determines if a user needs to complete email verification.
 *
 * @param user - The Firestore user document
 * @returns true if the user explicitly has emailVerified === false
 *
 * Note: Legacy users without the emailVerified field (undefined) are
 * considered verified for backward compatibility.
 */
function needsEmailVerification(user: FirestoreUser | null): boolean {
  return user?.emailVerified === false
}

/**
 * Route guard that redirects unverified users to the email verification page.
 *
 * This guard is applied to all authenticated routes except /verify-email.
 * It implements a "soft" verification requirement that only affects new users
 * who registered after the OTP feature was implemented.
 *
 * Verification Logic:
 * - emailVerified === false → Redirect to /verify-email (new unverified users)
 * - emailVerified === true → Allow access (verified users)
 * - emailVerified === undefined → Allow access (legacy users, backward compat)
 *
 * @example
 * // In AppRoutes.tsx:
 * <Route element={<EmailVerificationGuard />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 */
export default function EmailVerificationGuard() {
  const firestoreUser = useFirestoreUser()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!firestoreUser) return

    const shouldRedirect =
      needsEmailVerification(firestoreUser) &&
      location.pathname !== '/verify-email'

    if (shouldRedirect) {
      navigate('/verify-email', { replace: true })
    }
  }, [firestoreUser, location.pathname, navigate])

  // Show loading spinner while waiting for user data
  if (!firestoreUser) {
    return (
      <Center mt='200px'>
        <Spinner size='xl' />
      </Center>
    )
  }

  return <Outlet />
}
